/**
 * Time-zone-correct helpers. A screen always shows the masjid's local time, even if the device's own time zone
 * is set wrongly, so every calculation goes through the masjid's IANA zone rather than the device's.
 */

const partsCache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string): Intl.DateTimeFormat {
  let f = partsCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short',
    });
    partsCache.set(tz, f);
  }
  return f;
}

export interface ZonedParts { year: number; month: number; day: number; hour: number; minute: number; second: number; weekday: number }
const WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function zonedParts(date: Date, tz: string): ZonedParts {
  const out: Record<string, string> = {};
  for (const p of fmt(tz).formatToParts(date)) out[p.type] = p.value;
  return {
    year: +out.year!, month: +out.month!, day: +out.day!,
    hour: +out.hour! % 24, minute: +out.minute!, second: +out.second!, weekday: WD[out.weekday!] ?? 0,
  };
}

/** Offset of `tz` from UTC at `date`, in milliseconds (London in summer → +3 600 000). */
export function tzOffsetMs(date: Date, tz: string): number {
  const p = zonedParts(date, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

export const pad2 = (n: number) => String(n).padStart(2, '0');

/** Calendar date in the zone, as YYYY-MM-DD. */
export function ymd(date: Date, tz: string): string {
  const p = zonedParts(date, tz);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

export function parseYmd(s: string): { year: number; month: number; day: number } {
  const [y, m, d] = s.split('-').map(Number);
  return { year: y!, month: m!, day: d! };
}

export function addDays(day: string, n: number): string {
  const { year, month, day: d } = parseYmd(day);
  const t = new Date(Date.UTC(year, month - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** Day of week (0 = Sunday) for a calendar date. */
export function weekdayOf(day: string): number {
  const { year, month, day: d } = parseYmd(day);
  return new Date(Date.UTC(year, month - 1, d)).getUTCDay();
}

export const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** Normalises "5:4", "05.40", "540" or "5:40 pm" style input to HH:MM, or returns null. */
export function normaliseTime(input: string | undefined | null): string | null {
  if (input == null) return null;
  let s = String(input).trim().toLowerCase();
  if (!s) return null;
  const pm = /p\.?m\.?$/.test(s), am = /a\.?m\.?$/.test(s);
  s = s.replace(/\s*[ap]\.?m\.?$/, '').replace(/[.h]/g, ':');
  let h: number, m: number;
  if (/^\d{3,4}$/.test(s)) { h = +s.slice(0, -2); m = +s.slice(-2); }
  else {
    const mm = /^(\d{1,2})(?::(\d{1,2}))?(?::\d{1,2})?$/.exec(s);
    if (!mm) return null;
    h = +mm[1]!; m = mm[2] ? +mm[2] : 0;
  }
  if (pm && h < 12) h += 12;
  if (am && h === 12) h = 0;
  if (h > 23 || m > 59) return null;
  return `${pad2(h)}:${pad2(m)}`;
}

/** The instant at which the wall-clock time `hhmm` on `day` happens in `tz` (handles clock changes). */
export function zonedToInstant(day: string, hhmm: string, tz: string): Date {
  const { year, month, day: d } = parseYmd(day);
  const [h, m] = hhmm.split(':').map(Number);
  const guess = Date.UTC(year, month - 1, d, h!, m!);
  let t = guess - tzOffsetMs(new Date(guess), tz);
  // A second pass settles the rare case where the guess sits on the other side of a clock change.
  const off2 = tzOffsetMs(new Date(t), tz);
  t = guess - off2;
  return new Date(t);
}

/** HH:MM (or HH:MM:SS) of an instant in the zone. */
export function hhmm(date: Date, tz: string, opts: { seconds?: boolean; h12?: boolean } = {}): string {
  const p = zonedParts(date, tz);
  let h = p.hour;
  if (opts.h12) { h = h % 12; if (h === 0) h = 12; }
  return `${opts.h12 ? String(h) : pad2(h)}:${pad2(p.minute)}${opts.seconds ? ':' + pad2(p.second) : ''}`;
}

export function minutesOf(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h! * 60 + m!;
}
export function fromMinutes(total: number): string {
  const t = ((Math.round(total) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(t / 60))}:${pad2(t % 60)}`;
}

const dateFmtCache = new Map<string, Intl.DateTimeFormat>();
/** Locale date such as "Saturday 16 October" or "Saturday, 16 October 2026", in the masjid's zone. */
export function formatDate(date: Date, tz: string, style: 'long' | 'full' | 'short' = 'long'): string {
  const key = tz + style;
  let f = dateFmtCache.get(key);
  if (!f) {
    const o: Intl.DateTimeFormatOptions =
      style === 'full' ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
        : style === 'short' ? { weekday: 'short', day: 'numeric', month: 'short' }
          : { weekday: 'long', day: 'numeric', month: 'long' };
    f = new Intl.DateTimeFormat('en-GB', { ...o, timeZone: tz });
    dateFmtCache.set(key, f);
  }
  return f.format(date);
}

/** "5d 21h", "3h 12m", "18m", "45s" — compact, for countdowns. */
export function compactDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/** "01:23:45" style, for big countdown widgets. */
export function clockDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${pad2(m)}:${pad2(sec)}`;
}
